import { useCallback, useMemo } from 'react';

import { useRouter } from '#router/useRouter.js';
import { useShowCauseW_Selector } from '@givebacks/api/core';
import {
  Cart,
  storeKeys,
  useDeleteCartItem,
  useGetCart,
  useShowCart,
  useUpdateCartItemQuantity,
} from '@givebacks/api/store';
import { getErrorMessage } from '@givebacks/utils';
import { useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'core';
import { CartCard, Center, KebabMenu, Loader, notifications } from 'ui';

import { useShopStorage } from '../../../hooks/useShopStorage';

type CartContainerProps = {
  id: string;
  storeId: string;
  /** Slot for the checkout button */
  checkOutButton?: React.ReactNode;
  showGivingaDisclaimer?: boolean;
  disableCartItemNameClick?: boolean;
};

const useCartActions = () => {
  const { mutate: removeCartItem, isPending: isRemoveLoading } = useDeleteCartItem();
  const { mutate: updateCartQuantity, isPending: isQuantityLoading } = useUpdateCartItemQuantity();
  const isLoading = useMemo(
    () => isRemoveLoading || isQuantityLoading,
    [isRemoveLoading, isQuantityLoading]
  );

  return { updateCartQuantity, removeCartItem, isLoading };
};
const CartContainer = ({
  id,
  storeId,
  checkOutButton,
  showGivingaDisclaimer,
  disableCartItemNameClick = false,
}: CartContainerProps) => {
  const { useNavigate } = useRouter();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { updateShopCart, removeCart } = useShopStorage();

  const getCart = useGetCart();
  const { data: cartData, isLoading: isCartLoading } = useShowCart({
    cartId: id,
    causeId: storeId,
  });
  const { data: causeData } = useShowCauseW_Selector({ uuid: storeId });
  const { updateCartQuantity, removeCartItem, isLoading: isMutationsLoading } = useCartActions();

  const hasCartItems = useMemo(() => cartData?.cart_items.length, [cartData?.cart_items.length]);

  const handleRemoveCartItem = useCallback(
    ({ cartItemId }) => {
      removeCartItem(
        { cartId: id, cartItemId: cartItemId, causeId: storeId },
        {
          async onSuccess() {
            const vars = { storeId, cartId: id };
            if (cartData) {
              try {
                const data = await queryClient.fetchQuery({
                  queryKey: storeKeys.id('user').carts.id(id).detail,
                  queryFn: () => getCart({ cartId: id, causeId: storeId }),
                });
                if (data.cart_items_quantity === 0) {
                  removeCart(id);
                } else {
                  updateShopCart({ ...vars, cart: data });
                }
              } catch (error) {
                const axiosError = error as AxiosError;
                if (axiosError.response?.status === 404) {
                  removeCart(id);
                }
              }
            }
          },
          onError(error) {
            notifications.show({
              message: getErrorMessage(error, 'Error removing cart item'),
              variant: 'error',
            });
            const axiosError = error as AxiosError;
            if (axiosError.response?.status === 404) {
              removeCart(id);
            }
          },
        }
      );
    },
    [cartData, getCart, id, queryClient, removeCart, removeCartItem, storeId, updateShopCart]
  );

  const handleUpdateSuccess = useCallback(
    (cart: Cart) => {
      updateShopCart({ cartId: id, cart: cart, storeId });
    },
    [id, storeId, updateShopCart]
  );

  const handleQuantityChange = useCallback(
    ({ quantity, cartItemId }: { quantity: number; cartItemId: string }) => {
      if (quantity === 0) {
        return handleRemoveCartItem({ cartItemId });
      }
      updateCartQuantity(
        {
          cartId: id,
          cartItemId: cartItemId,
          quantity: quantity,
        },
        {
          onSuccess(data) {
            handleUpdateSuccess(data.cart);

            void queryClient.invalidateQueries({
              queryKey: storeKeys.id('user').consumer.checkout.all,
            });
          },
          onError(error) {
            // This shouldn't happen, but if it does, show an error notification and remove the cart
            // 404 errors are expected if the cart is no longer valid
            notifications.show({
              message: getErrorMessage(error, 'Error updating cart item'),
              variant: 'error',
            });
            const axiosError = error as AxiosError;
            if (axiosError.response?.status === 404) {
              removeCart(id);
            }
          },
        }
      );
    },
    [handleRemoveCartItem, handleUpdateSuccess, id, queryClient, removeCart, updateCartQuantity]
  );

  const options = useMemo(
    () =>
      KebabMenu.createItems([
        {
          label: 'Remove from cart',
          data: { cartId: id, uuid: '' },
          onClick: (data) => {
            if (!data) return;
            const cartItemId = data.uuid;
            handleRemoveCartItem({ cartItemId });
          },
          variant: 'destructive',
        },
      ]),
    [handleRemoveCartItem, id]
  );

  const isLoading = useMemo(
    () => isCartLoading || isMutationsLoading,
    [isCartLoading, isMutationsLoading]
  );

  const onCartItemNameClick = useCallback(
    (link: string) => {
      if (disableCartItemNameClick) {
        return;
      }

      navigate(link);
    },
    [disableCartItemNameClick, navigate]
  );

  // Note: this is used within a collapsible card, so we need to return a loader if the cart is not loaded otherwise
  // The collapsible card will not expand if the cart is not loaded, so we need to return a loader if the cart is not loaded.
  if (!hasCartItems || !cartData || !causeData)
    return (
      <Center>
        <Loader />
      </Center>
    );

  return (
    <CartCard
      cart={cartData}
      cartItems={cartData?.cart_items ?? []}
      subtotal={cartData?.subtotal ?? 0}
      storeData={{
        name: causeData?.name,
        imageSrc: causeData?.thumbnail?.url,
        shortener: causeData?.shortener,
      }}
      showGivingaDisclaimer={showGivingaDisclaimer}
      cartItemProps={{
        items: options,
      }}
      isLoading={isLoading}
      onQuantityChange={handleQuantityChange}
      isRecurring={cartData.recurring}
      onCartItemNameClick={onCartItemNameClick}
      checkOutButton={checkOutButton}
    />
  );
};

export { CartContainer };
